/**
 * Spare Parts — API client.
 * Mirrors src/lib/supabase/spareParts.ts function signatures exactly.
 */
import { apiGet, apiPost, apiPatch } from './client';
import type { SparePart, MoldSparePart, SparePartTransaction } from '@/types/spareParts';

export type { AddTransactionInput } from '@/lib/supabase/spareParts';

// ─── Queries ──────────────────────────────────────────────────────────────────

export async function fetchSpareParts(_orgId: string | null): Promise<SparePart[]> {
  return apiGet<SparePart[]>('/api/spare-parts');
}

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

export async function fetchSparePartsPaginated(
  _orgId: string | null,
  opts: { page?: number; pageSize?: number } = {},
): Promise<PaginatedResult<SparePart>> {
  const params = new URLSearchParams();
  params.set('page', String(opts.page ?? 1));
  params.set('pageSize', String(opts.pageSize ?? 25));
  return apiGet<PaginatedResult<SparePart>>(`/api/spare-parts?${params.toString()}`);
}

export async function fetchSparePart(id: string): Promise<SparePart | null> {
  try {
    return await apiGet<SparePart>(`/api/spare-parts/${id}`);
  } catch {
    return null;
  }
}

export async function fetchAllMoldSpareParts(_orgId: string | null): Promise<(MoldSparePart & { part: SparePart })[]> {
  // Fetched via mold-specific endpoints when needed; return empty for global store init
  return apiGet<(MoldSparePart & { part: SparePart })[]>('/api/spare-parts/mold-links');
}

export async function fetchMoldLinksForPart(
  partId: string,
): Promise<(MoldSparePart & { mold: { id: string; mold_id: string; name: string } })[]> {
  return apiGet(`/api/spare-parts/${partId}/mold-links`);
}

export async function fetchTransactionsByPart(
  partId: string,
): Promise<(SparePartTransaction & { performer_name?: string })[]> {
  return apiGet(`/api/spare-parts/${partId}/transactions`);
}

// ─── Mutations ────────────────────────────────────────────────────────────────

import type { AddTransactionInput as _AddTransactionInput } from '@/lib/supabase/spareParts';

export async function addTransactionInDB(
  partId: string,
  input: _AddTransactionInput,
  _orgId: string | null,
): Promise<void> {
  await apiPost<void>(`/api/spare-parts/${partId}/transactions`, {
    type: input.type,
    qty: input.qty,
    wo_id: input.woId,
    mold_id: input.moldId,
    notes: input.notes,
  });
}

export async function markPartOnOrder(
  partId: string,
  qty: number,
  expectedDate?: string,
  notes?: string,
): Promise<void> {
  await apiPatch<void>(`/api/spare-parts/${partId}/on-order`, {
    on_order: true,
    order_qty: qty,
    expected_delivery_date: expectedDate,
    order_notes: notes,
  });
}

export async function clearPartOrder(partId: string): Promise<void> {
  await apiPatch<void>(`/api/spare-parts/${partId}/on-order`, { on_order: false });
}

export async function updateSparePartCost(partId: string, unitCost: number | null): Promise<void> {
  await apiPatch<void>(`/api/spare-parts/${partId}/cost`, { unit_cost: unitCost });
}

export async function linkPartToMoldInDB(moldId: string, partId: string, notes?: string): Promise<void> {
  await apiPost<void>(`/api/molds/${moldId}/parts`, { part_id: partId, notes });
}

export interface CreateSparePartInput {
  part_id: string;
  part_name: string;
  category: string;
  unit: string;
  part_number?: string;
  description?: string;
  manufacturer?: string;
  supplier?: string;
  lead_time_days?: number;
  unit_cost?: number;
  stock_on_hand?: number;
  reorder_point?: number;
  reorder_qty?: number;
  storage_location_code?: string;
  is_critical?: boolean;
}

export interface UpdateSparePartInput {
  part_name?: string;
  category?: string;
  unit?: string;
  part_number?: string | null;
  description?: string | null;
  manufacturer?: string | null;
  supplier?: string | null;
  lead_time_days?: number | null;
  reorder_point?: number | null;
  reorder_qty?: number | null;
  storage_location_code?: string | null;
  is_critical?: boolean;
  status?: 'Active' | 'Obsolete';
}

export async function createSparePart(input: CreateSparePartInput): Promise<SparePart> {
  return apiPost<SparePart>('/api/spare-parts', input);
}

export async function updateSparePart(id: string, input: UpdateSparePartInput): Promise<void> {
  await apiPatch<void>(`/api/spare-parts/${id}`, input);
}
